import { createContext, useContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useChat } from '../hooks/useChat';
import { useDice } from '../hooks/useDice';
import { useRolls } from '../hooks/useRolls';
import { useChatSounds, type UseChatSoundsReturn } from '../hooks/useChatSounds';
import { useSessionContext } from './SessionContext';

type UseChatReturn = ReturnType<typeof useChat>;
type UseDiceReturn = ReturnType<typeof useDice>;
type UseRollsReturn = ReturnType<typeof useRolls>;

export interface SpeakingAsValue {
  name: string;
  color?: string;
}

export interface ChatContextValue {
  chat: UseChatReturn;
  dice: UseDiceReturn;
  rolls: UseRollsReturn;
  sounds: UseChatSoundsReturn;
  speakingAs: SpeakingAsValue | null;
  setSpeakingAs: (value: SpeakingAsValue | null) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { mp } = useSessionContext();
  const dice = useDice();
  const rawChat = useChat(dice);
  const rolls = useRolls(mp.send);
  const sounds = useChatSounds();
  const [speakingAs, setSpeakingAs] = useState<SpeakingAsValue | null>(null);

  // Multiplayer-aware chat send: routes messages through server when in session
  const mpChatSend = useCallback(
    (text: string) => {
      sounds.warmUp();
      if (!mp.session) {
        const trimmedLocal = text.trim();
        const partsLocal = trimmedLocal.split(/\s+/);
        const cmdLocal = partsLocal[0].toLowerCase();
        if ((cmdLocal === '/roll' || cmdLocal === '/r') && partsLocal.slice(1).join('')) {
          sounds.playDiceSound();
        } else if (!trimmedLocal.startsWith('/')) {
          sounds.playMessageSound();
        }
        rawChat.send(text);
        return;
      }
      const trimmed = text.trim();
      const cmd = trimmed.split(/\s+/)[0].toLowerCase();
      if (['/help', '/clear', '/name'].includes(cmd)) {
        rawChat.send(text);
        return;
      }
      if (cmd === '/roll' || cmd === '/r') {
        const formula = trimmed.split(/\s+/).slice(1).join('');
        if (formula) mp.send({ type: 'chat:roll', formula, ...(speakingAs ? { speakingAs } : {}) } as any);
        return;
      }
      mp.send({ type: 'chat:message', text: trimmed, ...(speakingAs ? { speakingAs } : {}) } as any);
    },
    [mp, rawChat, sounds, speakingAs],
  );

  const chat = useMemo(
    () => ({ ...rawChat, send: mpChatSend }),
    [rawChat, mpChatSend],
  );

  const value = useMemo(
    () => ({ chat, dice, rolls, sounds, speakingAs, setSpeakingAs }),
    [chat, dice, rolls, sounds, speakingAs],
  );

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}
